import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { ConfigConstants } from '../../../config/config-constants';
import { LoadStatus } from '../../load-status';
import {
    INPUT_FIELD_PROPERTIES_FEATURE_KEY,
    InputFieldPropertiesCollectionState,
    InputFieldPropertyState,
} from '../input-field-properties-collection.state';

/** Result of matching a field/screen/process query against the input-field-properties slice. */
export type InputFieldPropertyMatch = Readonly<{
    property?: InputFieldPropertyState;
    matchCount: number;
}>;

/**
 * File-private predicate for {@link selectInputFieldPropertyMatch}, mirroring the `.filter()` in
 * the legacy `InputFieldPropertiesService.getInputFieldProperties` (input-field-properties.service.ts:55-68).
 * `isNgmP58()` always evaluates to true in practice, so the process is always part of the match
 * (see the migration plan).
 */
function matchesQuery(
    property: InputFieldPropertyState,
    fieldName: string,
    screenName: string,
    process: ConfigConstants.PROCESS_INDICATOR,
): boolean {
    return property.fieldName === fieldName && property.screenName === screenName && property.process === process;
}

export const selectInputFieldPropertiesFeature = createFeatureSelector<InputFieldPropertiesCollectionState>(
    INPUT_FIELD_PROPERTIES_FEATURE_KEY,
);

export const selectInputFieldProperties: MemoizedSelector<object, InputFieldPropertyState[]> = createSelector(
    selectInputFieldPropertiesFeature,
    (state) => state.properties,
);

/** Raw load status of the slice. */
export const selectInputFieldPropertiesStatus: MemoizedSelector<object, LoadStatus> = createSelector(
    selectInputFieldPropertiesFeature,
    (state) => state.status,
);

/** Whether the input-field-properties slice has been populated from the backend. */
export const selectInputFieldPropertiesLoaded: MemoizedSelector<object, boolean> = createSelector(
    selectInputFieldPropertiesFeature,
    (state) => state.status === LoadStatus.Loaded,
);

/**
 * Look up the single property matching `fieldName`/`screenName`/`process`, carrying the entire
 * query from the legacy `InputFieldPropertiesService.getInputFieldProperties`: the count of
 * matches is returned alongside the property so the caller can pick between the "not a valid
 * identifier" (0 matches) and "not a unique identifier" (>1 matches) warnings.
 *
 * @param fieldName the field to look up
 * @param screenName the screen the field is placed on
 * @param process the business process to match
 */
export const selectInputFieldPropertyMatch = ({
    fieldName,
    screenName,
    process,
}: {
    fieldName: string;
    screenName: string;
    process: ConfigConstants.PROCESS_INDICATOR;
}): MemoizedSelector<object, InputFieldPropertyMatch> =>
    createSelector(selectInputFieldProperties, (properties) => {
        const matches = properties.filter((property) => matchesQuery(property, fieldName, screenName, process));
        return { property: matches.length === 1 ? matches[0] : undefined, matchCount: matches.length };
    });
